import { useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  PersonRegular,
  SparkleRegular,
  PeopleTeamRegular,
  GridRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';

const MAX_CHARS = 1000;

/* Fluent UI icons for feature cards */
const featureIcons = {
  person: <PersonRegular style={{ fontSize: 24, color: '#0078d4' }} />,
  sparkle: <SparkleRegular style={{ fontSize: 24, color: '#0078d4' }} />,
  escalate: <PeopleTeamRegular style={{ fontSize: 24, color: '#0078d4' }} />,
  grid: <GridRegular style={{ fontSize: 24, color: '#0078d4' }} />,
  shield: <ShieldCheckmarkRegular style={{ fontSize: 14, color: '#6b7280' }} />,
  sparkleSm: <SparkleRegular style={{ fontSize: 16, color: '#1d4ed8' }} />,
  sparkleWhite: <SparkleRegular style={{ fontSize: 16, color: 'white' }} />,
};

const featureCards = [
  { icon: featureIcons.person, title: 'No sign-in required to get started', desc: 'Jump in and get help right away.' },
  { icon: featureIcons.sparkle, title: 'AI-guided troubleshooting', desc: 'Get step-by-step solutions faster with AI.' },
  { icon: featureIcons.grid, title: 'Works across Microsoft products', desc: 'One place for help across Microsoft.' },
];

/* Microsoft product icons served from /icons/ in public folder */
const productImages: Record<string, string> = {
  Teams: '/icons/teams.png',
  Outlook: '/icons/outlook.png',
  'Microsoft 365': '/icons/m365.png',
  Surface: '/icons/surface.png',
  Xbox: '/icons/xbox.png',
  Windows: '/icons/windows.png',
  OneDrive: '/icons/onedrive.png',
  Copilot: '/icons/copilot.png',
  'Microsoft Store': '/icons/msstore.png',
  'Microsoft account': '/icons/msaccount.png',
};

const supportedProducts = [
  'Microsoft 365', 'Copilot', 'Outlook', 'OneDrive', 'Teams',
  'Windows', 'Surface', 'Xbox', 'Microsoft Store',
];

const trendingIssues = [
  { product: 'Teams', icon: '/icons/teams.png', title: "Can't join Teams meetings", desc: 'Some users may have trouble joining meetings or calls.' },
  { product: 'Outlook', icon: '/icons/outlook.png', title: 'Emails are slow to sync', desc: 'Sync may take longer than usual across devices.' },
  { product: 'Xbox', icon: '/icons/xbox.png', title: 'Trouble signing in', desc: 'Some users may need help getting back into their account.' },
  { product: 'Microsoft 365', icon: '/icons/m365.png', title: 'Apps running slowly', desc: 'Some features may be slower than expected.' },
];

/**
 * Landing page with hero, textarea to ask the AI assistant,
 * feature highlights, and supported-product icons.
 */
export function LandingPage({ onAskAssistant, onNavigate }: { onAskAssistant: (message: string) => void; onNavigate?: (target: string) => void }) {
  const [query, setQuery] = useState('');
  const isMobile = useIsMobile();

  const handleAsk = () => {
    const trimmed = query.trim();
    if (!trimmed) return;
    onAskAssistant(trimmed);
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  return (
    <div style={{
      ...styles.pageOuter,
      ...(isMobile ? { gridTemplateColumns: '1fr', padding: '16px 12px 32px', gap: 16 } : {}),
    }}>
      {/* ── Trending issues sidebar ── */}
      <aside style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div>
            <h3 style={styles.sidebarTitle}>Popular fixes right now</h3>
            <p style={styles.sidebarSub}>Quick solutions to common problems.</p>
          </div>
          <span style={{ display: 'inline-flex' }}>{featureIcons.sparkleSm}</span>
        </div>
        {trendingIssues.map((issue) => (
          <div key={issue.title} style={styles.issueCard}>
            <img src={issue.icon} alt={issue.product} style={styles.issueIcon} />
            <div>
              <p style={styles.issueProduct}>{issue.product}</p>
              <p style={styles.issueTitle}>{issue.title}</p>
              <p style={styles.issueDesc}>{issue.desc}</p>
              <button
                className="hover-solution-link"
                style={styles.issueLink}
                onClick={() => onAskAssistant(`I need help with ${issue.product}: ${issue.title}`)}
              >
                View solution &rsaquo;
              </button>
            </div>
          </div>
        ))}
        <button
          className="hover-solution-link"
          style={styles.issueLink}
          onClick={() => onAskAssistant('Show me more common solutions')}
        >
          See more solutions &rsaquo;
        </button>
      </aside>

      {/* ── Main content ── */}
      <div style={styles.page}>
      {/* ── Hero ── */}
      <section style={styles.heroCard}>
        <div style={styles.heroGrid}>
          {/* Left column */}
          <div style={styles.heroLeft}>
            <span style={styles.badge}><span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>{featureIcons.sparkleSm}</span> AI-FIRST SUPPORT</span>
            <h1 style={styles.heroTitle}>
              Get help for Microsoft products
            </h1>
            <p style={styles.heroDesc}>
              Describe your issue and our AI Assistant will help find the right solution.
              You can chat, get guided troubleshooting, or connect with a support agent.
            </p>
          </div>

          {/* Right column */}
          <div style={styles.heroRight}>
            <h2 style={styles.askHeading}><span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 6 }}>{featureIcons.sparkleSm}</span>What can we help you with today?</h2>

            <div style={styles.textareaWrap}>
              <textarea
                value={query}
                onChange={(e) => {
                  if (e.target.value.length <= MAX_CHARS) setQuery(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                placeholder="Describe your issue—e.g., I can't join a Teams meeting, Outlook keeps asking for my password, Xbox sign-in issue"
                style={styles.textarea}
                rows={4}
              />
              <span style={styles.charCount}>{query.length}/{MAX_CHARS}</span>
            </div>

            <div style={styles.buttonRow}>
              <button onClick={handleAsk} className="hover-zoom" style={styles.askButton} disabled={!query.trim()}>
                <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>{featureIcons.sparkleWhite}</span> Ask AI Assistant
              </button>
            </div>

            <p style={styles.hint}>
              <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>{featureIcons.shield}</span>
              You can get started without signing in. Some features may be limited.
            </p>
          </div>
        </div>
      </section>

      {/* ── Feature cards ── */}
      <section style={styles.featuresRow}>
        {featureCards.map((f) => (
          <div key={f.title} style={styles.featureCard}>
            <span style={styles.featureIcon}>{f.icon}</span>
            <strong style={styles.featureTitle}>{f.title}</strong>
            <span style={styles.featureDesc}>{f.desc}</span>
          </div>
        ))}
      </section>

      {/* ── Supported products ── */}
      <section style={styles.productsSection}>
        <div>
          <h3 style={styles.productsHeading}>Supported products</h3>
          <p style={styles.productsSub}>Get help for the products you use every day.</p>
        </div>
        <div style={styles.productsRow}>
          {supportedProducts.map((name) => (
            <div
              key={name}
              className="hover-zoom"
              style={styles.productChip}
              onClick={() => {
                if (name === 'Teams') {
                  onNavigate?.('teams');
                } else {
                  onAskAssistant(`I need help with ${name}`);
                }
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (name === 'Teams') {
                    onNavigate?.('teams');
                  } else {
                    onAskAssistant(`I need help with ${name}`);
                  }
                }
              }}
            >
              <img src={productImages[name]} alt={name} style={styles.productIcon} />
              <span style={styles.productName}>{name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={styles.footer}>
        <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>{featureIcons.shield}</span>
        Your privacy is important to us.{' '}
        <a href="https://privacy.microsoft.com" target="_blank" rel="noreferrer" style={styles.footerLink}>
          Learn more
        </a>{' '}
        about how we protect your data.
      </footer>
      </div>
    </div>
  );
}

// ── Styles ──

const styles: Record<string, React.CSSProperties> = {
  pageOuter: {
    display: 'grid',
    gridTemplateColumns: '280px minmax(0, 1fr)',
    gap: 24,
    maxWidth: 1400,
    margin: '0 auto',
    padding: '32px 24px 48px',
    alignItems: 'start',
  },

  /* Sidebar */
  sidebar: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  sidebarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  sidebarTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  sidebarSub: {
    fontSize: 13,
    color: '#6b7280',
    margin: '2px 0 0',
  },
  issueCard: {
    display: 'flex',
    gap: 12,
    padding: '16px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  issueIcon: {
    width: 36,
    height: 36,
    objectFit: 'contain',
    flexShrink: 0,
  },
  issueProduct: {
    fontSize: 12,
    color: '#6b7280',
    margin: 0,
    fontWeight: 500,
  },
  issueTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
    margin: '2px 0 4px',
  },
  issueDesc: {
    fontSize: 12,
    color: '#6b7280',
    margin: '0 0 6px',
    lineHeight: 1.4,
  },
  issueLink: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0078d4',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    textAlign: 'left',
  },

  /* Main content */
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },

  /* Hero */
  heroCard: {
    background: 'white',
    borderRadius: 16,
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
  },
  heroGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
    minHeight: 360,
  },
  heroLeft: {
    minWidth: 0,
    padding: '48px 40px',
    background: 'linear-gradient(135deg, #eef4ff 0%, #dbe8ff 50%, #e8e0f7 100%)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 16,
  },
  badge: {
    display: 'inline-block',
    width: 'fit-content',
    background: '#dbeafe',
    color: '#1d4ed8',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.5,
    padding: '5px 14px',
    borderRadius: 999,
  },
  heroTitle: {
    fontSize: 30,
    fontWeight: 700,
    lineHeight: 1.25,
    color: '#111827',
    margin: 0,
  },
  heroDesc: {
    fontSize: 15,
    color: '#4b5563',
    lineHeight: 1.6,
    margin: 0,
  },
  heroRight: {
    minWidth: 0,
    padding: '40px 40px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 16,
  },
  askHeading: {
    fontSize: 18,
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  textareaWrap: {
    position: 'relative',
  },
  textarea: {
    width: '100%',
    padding: '16px 18px 28px',
    fontSize: 15,
    fontFamily: 'inherit',
    border: '1px solid #d1d5db',
    borderRadius: 10,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
    minHeight: 120,
    color: '#374151',
  },
  charCount: {
    position: 'absolute',
    bottom: 10,
    right: 14,
    fontSize: 12,
    color: '#9ca3af',
  },
  buttonRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
  askButton: {
    padding: '12px 28px',
    background: '#0078d4',
    color: 'white',
    border: 'none',
    borderRadius: 999,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  hint: {
    fontSize: 13,
    color: '#6b7280',
    margin: 0,
  },

  /* Feature cards */
  featuresRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
    gap: 16,
  },
  featureCard: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  featureIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: 10,
    background: '#eef4ff',
    marginBottom: 4,
  },
  featureTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111827',
  },
  featureDesc: {
    fontSize: 13,
    color: '#6b7280',
    lineHeight: 1.5,
  },

  /* Supported products */
  productsSection: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '28px 32px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
  },
  productsHeading: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    margin: '0 0 4px',
  },
  productsSub: {
    fontSize: 14,
    color: '#6b7280',
    margin: 0,
  },
  productsRow: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  productChip: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    width: 80,
    textAlign: 'center',
    cursor: 'pointer',
  },
  productName: {
    fontSize: 12,
    color: '#374151',
    fontWeight: 500,
  },
  productIcon: {
    width: 40,
    height: 40,
    objectFit: 'contain',
  },

  /* Footer */
  footer: {
    textAlign: 'center',
    fontSize: 13,
    color: '#6b7280',
    padding: '12px 0',
  },
  footerLink: {
    color: '#0078d4',
    textDecoration: 'underline',
  },
};
